import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Space, Row, Col, Form, Input, InputNumber, DatePicker, Select, Switch, Cascader } from 'antd';
import { SearchOutlined, ClearOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ForgeQueryBuilderProps, ForgeQuery, FieldConfig } from './types';
import { buildForgeQueryFromValues, forgeQueryToFormValues } from './query';
import { clearRememberedValues, loadRememberedValues, saveRememberedValues } from './remember';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DateRangeField = ({ value, onChange, field, values }: any) => {
  const [dates, setDates] = useState<any>(null);

  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setDates(null);
    }
  }, [value]);

  return (
    <RangePicker
      value={value}
      onChange={onChange}
      onCalendarChange={(val: any) => setDates(val)}
      presets={field.presets}
      placeholder={Array.isArray(field.placeholder) ? field.placeholder : ['开始日期', '结束日期']}
      format={field.format || 'YYYY-MM-DD'}
      style={{ width: field.width || '100%' }}
      disabledDate={(current) => field.disabledDate ? field.disabledDate(current, values, dates) : false}
    />
  );
};

const ForgeQueryBuilder: React.FC<ForgeQueryBuilderProps> = ({
  fields,
  defaultQuery,
  onChange,
  onSearch,
  onExport,
  showSort = false,
  showPagination = false,
  className,
  style,
  ignoreRemembered = false
}) => {
  const [form] = Form.useForm();
  const [queryValues, setQueryValues] = useState<Record<string, any>>({});
  const didInitRef = useRef(false);
  const suppressPersistRef = useRef(false);

  // 初始化表单值
  useEffect(() => {
    if (didInitRef.current) return;
    if (!fields || fields.length === 0) return;
    didInitRef.current = true;

    const fromDefault = forgeQueryToFormValues(fields, defaultQuery);
    const remembered = ignoreRemembered ? {} : loadRememberedValues(fields);
    const merged = { ...fromDefault, ...remembered };

    suppressPersistRef.current = true;
    setQueryValues(merged);
    form.setFieldsValue(merged);
    Promise.resolve().then(() => {
      suppressPersistRef.current = false;
    });
  }, [defaultQuery, fields, form]);

  // 构建查询对象：统一过滤空数组、空字符串、无效范围
  const buildQueryFromValues = useCallback((values: Record<string, any>): ForgeQuery => {
    return buildForgeQueryFromValues(fields, values);
  }, [fields]);

  // 当表单值变化时，通知父组件（不触发查询，仅同步构建的 query）
  const notifyChange = useCallback((values: Record<string, any>) => {
    const query = buildQueryFromValues(values);
    onChange?.(query);
  }, [buildQueryFromValues, onChange]);

  // 表单值变化处理
  const handleValuesChange = (changedValues: any, allValues: any) => {
    setQueryValues(allValues);
    notifyChange(allValues);
    if (!suppressPersistRef.current) {
      saveRememberedValues(fields, allValues);
    }
  };

  // 清空表单并执行搜索
  const handleClear = () => {
    form.resetFields();
    setQueryValues({});
    clearRememberedValues(fields);
    const emptyQuery = buildQueryFromValues({});
    notifyChange({});
    onSearch?.(emptyQuery);
  };

  // 执行搜索
  const handleSearch = () => {
    form.validateFields().then(() => {
      const query = buildQueryFromValues(queryValues);
      onSearch?.(query);
    }).catch(() => {
      // ignore
    });
  };

  // 导出
  const handleExport = () => {
    form.validateFields().then(() => {
      const query = buildQueryFromValues(queryValues);
      onExport?.(query);
    }).catch(() => {
      // ignore
    });
  };

  // 渲染字段输入组件
  const renderFieldInput = (field: FieldConfig) => {
    switch (field.type) {
      case 'string':
        return (
          <Input
            placeholder={field.placeholder as any || `请输入${field.label}`}
            allowClear
            onPressEnter={handleSearch}
          />
        );

      case 'number':
        // 检查是否支持范围查询
        const supportsRange = field.operators?.includes('between');
        if (supportsRange) {
          return (
            <Input.Group compact>
              <InputNumber
                placeholder="最小值"
                style={{ width: '50%' }}
                onPressEnter={handleSearch}
              />
              <InputNumber
                placeholder="最大值"
                style={{ width: '50%' }}
                onPressEnter={handleSearch}
              />
            </Input.Group>
          );
        }
        return (
          <InputNumber
            placeholder={field.placeholder as any || `请输入${field.label}`}
            style={{ width: '100%' }}
            onPressEnter={handleSearch}
          />
        );

      case 'date':
        return (
          <DateRangeField field={field} values={queryValues} />
        );

      case 'cascader':
        return (
          <Cascader
            options={(field.getCascaderOptions ? field.getCascaderOptions(queryValues) : (field as any).cascaderOptions) as any}
            placeholder={(field.placeholder as string) || `请选择${field.label}`}
            style={{ width: field.width || '100%' }}
            {...(field.cascaderProps || {})}
          />
        );

      case 'boolean':
        return (
          <Select placeholder={`请选择${field.label}`} allowClear>
            <Option value={true}>是</Option>
            <Option value={false}>否</Option>
          </Select>
        );

      case 'select':
        return (
          <Select
            placeholder={field.placeholder || `请选择${field.label}`}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: field.width }}
            options={(field.getOptions ? field.getOptions(queryValues) : field.options)?.map(opt => ({ label: opt.label, value: opt.value }))}
            onChange={(val) => {
              const linked = field.onValueChange?.(val, form.getFieldsValue());
              if (linked && typeof linked === 'object') {
                form.setFieldsValue({ [field.key]: val, ...linked });
              } else {
                form.setFieldsValue({ [field.key]: val });
              }
            }}
          />
        );

      case 'multiSelect':
        return (
          <Select
            mode="multiple"
            placeholder={field.placeholder || `请选择${field.label}`}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: field.width }}
            options={(field.getOptions ? field.getOptions(queryValues) : field.options)?.map(opt => ({ label: opt.label, value: opt.value }))}
            onChange={(val) => {
              const linked = field.onValueChange?.(val, form.getFieldsValue());
              if (linked && typeof linked === 'object') {
                form.setFieldsValue({ [field.key]: val, ...linked });
              } else {
                form.setFieldsValue({ [field.key]: val });
              }
            }}
          />
        );

      default:
        return (
          <Input
            placeholder={field.placeholder as any || `请输入${field.label}`}
            allowClear
            onPressEnter={handleSearch}
          />
        );
    }
  };

  return (
    <Card
      className={className}
      style={{
        ...style,
        borderRadius: 8,
        padding: 8,
      }}
      size="small"
    >
      <Form
        form={form}
        layout="inline"
        onValuesChange={handleValuesChange}
        style={{ width: '100%' }}
      >
        <Row gutter={16} align="middle" style={{ width: '100%' }}>
          {fields.map((field) => (
            <Col key={field.key}>
              <Form.Item
                name={field.key}
                label={field.label}
                style={{ marginBottom: 12 }}
                rules={field.required ? [{ required: true, message: `请输入${field.label}` }] : undefined}
                {...(field.type === 'cascader' && field.onValueChange
                  ? {
                    getValueFromEvent: (...args: any[]) => {
                      const val = args?.[0];
                      const linked = field.onValueChange?.(val, form.getFieldsValue());
                      if (linked && typeof linked === 'object' && field.key in linked) return (linked as any)[field.key];
                      return val;
                    },
                  }
                  : {})}
              >
                {renderFieldInput(field)}
              </Form.Item>
            </Col>
          ))}
          <Col flex="none">
            <Form.Item style={{ marginBottom: 12 }}>
              <Space>
                <Button icon={<ClearOutlined />} onClick={handleClear}>重置</Button>
                {onExport && <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>}
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default ForgeQueryBuilder;
